import React, { useContext } from "react";
import PropTypes from "prop-types";
import { useTheme } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableFooter from "@mui/material/TableFooter";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import IconButton from "@mui/material/IconButton";
import FirstPageIcon from "@mui/icons-material/FirstPage";
import KeyboardArrowLeft from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRight from "@mui/icons-material/KeyboardArrowRight";
import LastPageIcon from "@mui/icons-material/LastPage";
import Tooltip from "@mui/material/Tooltip";
import Chip from "@mui/material/Chip";

import { PDFDownloadLink } from "@react-pdf/renderer";
import PDFReceipt from "../Tools/PDFReceipt";

import { AppContext } from "../contextApp";

function TablePaginationActions(props) {
  const theme = useTheme();
  const { count, page, rowsPerPage, onPageChange } = props;

  const handleFirstPageButtonClick = (event) => {
    onPageChange(event, 0);
  };

  const handleBackButtonClick = (event) => {
    onPageChange(event, page - 1);
  };

  const handleNextButtonClick = (event) => {
    onPageChange(event, page + 1);
  };

  const handleLastPageButtonClick = (event) => {
    onPageChange(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1));
  };

  return (
    <Box sx={{ flexShrink: 0, ml: 2.5 }}>
      <IconButton
        onClick={handleFirstPageButtonClick}
        disabled={page === 0}
        aria-label="first page"
      >
        {theme.direction === "rtl" ? <LastPageIcon /> : <FirstPageIcon />}
      </IconButton>
      <IconButton onClick={handleBackButtonClick} disabled={page === 0} aria-label="previous page">
        {theme.direction === "rtl" ? <KeyboardArrowRight /> : <KeyboardArrowLeft />}
      </IconButton>
      <IconButton
        onClick={handleNextButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="next page"
      >
        {theme.direction === "rtl" ? <KeyboardArrowLeft /> : <KeyboardArrowRight />}
      </IconButton>
      <IconButton
        onClick={handleLastPageButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="last page"
      >
        {theme.direction === "rtl" ? <FirstPageIcon /> : <LastPageIcon />}
      </IconButton>
    </Box>
  );
}

TablePaginationActions.propTypes = {
  count: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
  page: PropTypes.number.isRequired,
  rowsPerPage: PropTypes.number.isRequired,
};

export default function TableHistoryCustom({ fields, data, actionType = [], action, action2 = null, action3 = null }) {
  const { studentData, setStudentData } = useContext(AppContext);
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(20);

  const rows = data.sort((a, b) => (a.name < b.name ? -1 : 1));

  // Avoid a layout jump when reaching the last page with empty rows.
  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - rows.length) : 0;

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const actionColCount = actionType.length;
  const totalCols = fields.length + actionColCount;

  const iconBtn = (onClick, title, iconClass, hoverBg, borderColor, baseBg, baseColor, label) => {
    const isRed = hoverBg === '#fee2e2';
    const isGreen = hoverBg === '#dcfce7';
    const isAmber = hoverBg === '#fef9c3';
    const isSlate = hoverBg === '#f1f5f9';
    const border = borderColor || (isRed ? '#dc2626' : isGreen ? '#16a34a' : isAmber ? '#d97706' : '#94a3b8');
    const bg = baseBg || (isRed ? '#fef2f2' : isGreen ? '#f0fdf4' : isAmber ? '#fffbeb' : '#f8fafc');
    const color = baseColor || (isRed ? '#dc2626' : isGreen ? '#16a34a' : isAmber ? '#d97706' : '#475569');
    return (
      <Tooltip title={title}>
        <button
          onClick={onClick}
          style={{
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            gap: '4px', padding: label ? '4px 10px' : '4px 8px',
            borderRadius: '5px', border: `1px solid ${border}`,
            backgroundColor: bg, cursor: 'pointer',
            transition: 'all 0.12s', fontSize: '0.7rem', fontWeight: 600, color,
          }}
          onMouseEnter={e => {
            e.currentTarget.style.backgroundColor = (isRed ? '#dc2626' : isGreen ? '#16a34a' : isAmber ? '#d97706' : '#475569');
            e.currentTarget.style.color = '#fff';
            const i = e.currentTarget.querySelector('i');
            if (i) i.style.color = '#fff';
          }}
          onMouseLeave={e => {
            e.currentTarget.style.backgroundColor = bg;
            e.currentTarget.style.color = color;
            const i = e.currentTarget.querySelector('i');
            if (i) i.style.color = color;
          }}
        >
          <i className={iconClass} style={{ fontSize: '11px', color }}></i>
          {label && label}
        </button>
      </Tooltip>
    );
  };

  const statusBadge = (value) => {
    const isGateway = ["approved","rejected","in_process","in_mediation","cancelled","refunded","pending"].includes(value);
    const label = isGateway ? "Pendiente" : value;
    const isSuccess = value === "Aprobado" || value === "success" || value === "approved";
    const isError = value === "Declinado" || value === "error" || value === "rejected" || value === "cancelled";
    const bg = isSuccess ? '#dcfce7' : isError ? '#fee2e2' : '#fef9c3';
    const color = isSuccess ? '#166534' : isError ? '#991b1b' : '#854d0e';
    return (
      <span style={{
        display: 'inline-flex', alignItems: 'center', gap: '4px',
        padding: '2px 8px', borderRadius: '9999px', fontSize: '0.7rem',
        fontWeight: 600, backgroundColor: bg, color,
      }}>
        {label}
        {isGateway && (
          <span style={{
            padding: '1px 5px', borderRadius: '9999px', fontSize: '0.6rem',
            backgroundColor: 'rgba(0,0,0,0.08)', color,
          }}>
            {value}
          </span>
        )}
      </span>
    );
  };

  return (
    <TableContainer
      component={Paper}
      sx={{
        borderRadius: '8px', overflowX: 'auto',
        boxShadow: '0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.08)',
        border: '1px solid #e2e8f0',
      }}
    >
      <Table size="small" sx={{ minWidth: 500 }} aria-label="custom pagination table">
        <TableHead>
          <TableRow sx={{
            '& th': {
              backgroundColor: '#111827', color: '#f9fafb', fontWeight: 700,
              fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: '0.06em',
              borderBottom: '2px solid #dc2626', py: 1.5, whiteSpace: 'nowrap',
            },
          }}>
            {fields.map((row) => (
              <TableCell key={row.label}>{row.label}</TableCell>
            ))}
            {actionColCount > 0 && <TableCell sx={{ whiteSpace: 'nowrap', minWidth: '120px' }}>Acciones</TableCell>}
          </TableRow>
        </TableHead>
        <TableBody>
          {(rowsPerPage > 0
            ? rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
            : rows
          ).map((row, index) => (
            <TableRow
              key={row.id}
              sx={{
                backgroundColor: index % 2 === 0 ? '#ffffff' : '#f8fafc',
                '&:hover': { backgroundColor: '#fef2f2' },
                transition: 'background-color 0.12s ease',
                ...(parseInt(row["status"]) === 1 ? { borderLeft: '3px solid #fca5a5' } : {}),
                '& td': { borderBottom: '1px solid #f1f5f9', py: 1, fontSize: '0.82rem' },
              }}
            >
              {fields.map((item) => {
                if (item.name === "status_description" || item.name === "desc_2") {
                  return (
                    <TableCell key={item.name}>
                      {statusBadge(row[item.name])}
                    </TableCell>
                  );
                }
                return (
                  <TableCell key={item.name}>{row[item.name]}</TableCell>
                );
              })}

              {(actionType.includes("cancel") || actionType.includes("downloadEvidence") || actionType.includes("downloadFileEntry") || actionType.includes("approve") || actionType.includes("receipt") || actionType.includes("edit")) && (
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flexWrap: 'nowrap' }}>
                    {actionType.includes("cancel") &&
                      iconBtn(() => action(row), "Cancelar", "fa-solid fa-circle-xmark", "#fee2e2", null, null, null, "Cancelar")}

                    {actionType.includes("downloadEvidence") && row.method_payment === 'Transferencia' &&
                      iconBtn(() => action2(row), "Descargar evidencia", "fa-solid fa-file-arrow-down", "#dcfce7", null, null, null, "Evidencia")}

                    {actionType.includes("downloadFileEntry") && row.file !== null &&
                      iconBtn(() => action2(row), "Descargar evidencia", "fa-solid fa-file-arrow-down", "#dcfce7", null, null, null, "Evidencia")}

                    {actionType.includes("approve") &&
                      iconBtn(() => action(row, 'declinar'), "Declinar",
                        parseInt(row.status) === 2
                          ? "fa-solid fa-circle-xmark"
                          : "fa-solid fa-circle-xmark",
                        "#fee2e2", null,
                        parseInt(row.status) === 2 ? '#fef2f2' : '#f8fafc',
                        parseInt(row.status) === 2 ? '#dc2626' : '#94a3b8',
                        "Declinar")}

                    {actionType.includes("approve") &&
                      iconBtn(() => action(row, 'aprobar'), "Aprobar",
                        parseInt(row.status) === 0
                          ? "fa-solid fa-check-circle"
                          : "fa-solid fa-check-circle",
                        "#dcfce7", null,
                        parseInt(row.status) === 0 ? '#f0fdf4' : '#f8fafc',
                        parseInt(row.status) === 0 ? '#16a34a' : '#94a3b8',
                        "Aprobar")}

                    {actionType.includes("receipt") && row.status_description === 'Aprobado' && (
                      <PDFDownloadLink
                        document={
                          <PDFReceipt
                            name={studentData.name ?? ""}
                            curp={studentData.curp ?? ""}
                            item={row}
                            description={`${row.description} año ${row.year}`}
                          />
                        }
                        fileName={`Recibo_${row.description}`}
                      >
                        {iconBtn(null, "Recibo", "fa-solid fa-file", "#f1f5f9", null, null, null, "Recibo")}
                      </PDFDownloadLink>
                    )}

                    {actionType.includes("edit") &&
                      iconBtn(() => action3(row), "Editar Pago", "fa-solid fa-pen-to-square", "#fef9c3", null, null, null, "Editar")}
                  </div>
                </TableCell>
              )}
            </TableRow>
          ))}

          {emptyRows > 0 && (
            <TableRow style={{ height: 45 * emptyRows }}>
              <TableCell colSpan={totalCols} />
            </TableRow>
          )}
        </TableBody>
        <TableFooter>
          <TableRow sx={{ '& td': { borderBottom: 'none', backgroundColor: '#f8fafc' } }}>
            <TablePagination
              rowsPerPageOptions={[5, 10, 20, 25, { label: "Todos", value: -1 }]}
              colSpan={totalCols}
              count={rows.length}
              rowsPerPage={rowsPerPage}
              page={page}
              SelectProps={{ inputProps: { "aria-label": "filas por página" }, native: true }}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              ActionsComponent={TablePaginationActions}
              sx={{
                '& .MuiTablePagination-toolbar': { fontSize: '0.78rem' },
                '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': { fontSize: '0.78rem' },
              }}
            />
          </TableRow>
        </TableFooter>
      </Table>
    </TableContainer>
  );
}
